import styles from "./DefaultFormSelectField.module.scss";
import { Controller, useFormContext } from "react-hook-form";
import { classNames } from "primereact/utils";
import { VirtualScrollerProps } from "primereact/virtualscroller";
import { ReactNode } from "react";
import { RenderWhen } from "../../../../infrastructure/components/RenderWhen";
import { useSelectField } from "./hooks/useSelectField";
import { SkeletonItem } from "../../../skeletons/SkeletonItem";
import { AppMultiSelectDropDown } from "./abstract/AppMultiselectDropDown";
import { useAppContext } from "../../../../../AppContext";
import { AppTooltip } from "../../../tooltip/AppTooltip";

import { dvSize } from "../../../../infrastructure/utilities/viewportUnitsUtility";
interface IDefaultFormMultiSelectFieldProps<TOption> {
  name: string;
  options: TOption[] | undefined;
  tooltip?: string;
  hasGroupedOptions?: boolean;
  isLoading?: boolean;
  disabled?: boolean;
  className?: string;
  filter?: boolean;
  filterBy?: string;
  editable?: boolean;
  width?: string;
  defaultValue?: number[];
  label?: string;
  required?: boolean;
  includeId?: boolean;
  selectAllOption?: { id: number; name: string };
  noneOption?: { id: number; name: string };
  emptyMessage?: string;
  display?: "chip" | "comma";
  showPanelHeader?: boolean;
  virtualScrollerOptions?: VirtualScrollerProps;
  optionDisabled?: string;
  /**
   * Custom renderer for one option row. A virtualised list pins every row to
   * `virtualScrollerOptions.itemSize`, so the template must render at a fixed height.
   */
  itemTemplate?: (option: TOption) => ReactNode;
  /** Class applied to the overlay panel, which is portalled outside this component's tree. */
  panelClassName?: string;
  /** Values kept in the field but neither offered nor shown (see AppMultiSelectDropDown). */
  hiddenValues?: number[];
  /** Past this many selections the field shows `selectedItemsLabel` instead of the chips. */
  maxSelectedLabels?: number;
  /** Text shown past `maxSelectedLabels`; "{0}" is replaced with the count. */
  selectedItemsLabel?: string;
}

export const DefaultFormMultiSelectField = <TOption,>({
  name,
  options,
  tooltip,
  hasGroupedOptions,
  isLoading = false,
  disabled,
  className,
  filter = true,
  filterBy,
  editable = false,
  width,
  defaultValue,
  label,
  required,
  includeId,
  selectAllOption,
  noneOption,
  emptyMessage,
  display,
  showPanelHeader,
  virtualScrollerOptions,
  optionDisabled,
  itemTemplate,
  panelClassName,
  hiddenValues,
  maxSelectedLabels,
  selectedItemsLabel,
}: IDefaultFormMultiSelectFieldProps<TOption>) => {
  const { control } = useFormContext();
  const { isFocused, onFocus, onBlur } = useSelectField();
  const { isDesktopOrTablet } = useAppContext();

  if (!width) {
    if (isDesktopOrTablet) {
      width = "600px";
    } else {
      width = dvSize("80dvw");
    }
  }

  if (!options) {
    <></>;
  }
  return (
    <Controller
      name={name}
      control={control}
      render={({ field, fieldState }) => (
        <div
          style={{ width: width, maxWidth: width }}
          className={classNames(styles.root, className, {
            // The single-select field has always done this; the multiselect simply never
            // did, so a disabled one still lit up on hover and read as clickable.
            [styles.root_disabled]: disabled,
          })}
        >
          <RenderWhen condition={!!label}>
            <div
              className={classNames(styles.root_label, {
                [styles.root_label_interacted_with]:
                  isFocused || field.value !== null,
                [styles.root_label_error]: !!fieldState.error?.message,
              })}
            >
              {label}
              {required ? "*" : ""}
              <AppTooltip content={tooltip} />
            </div>
          </RenderWhen>

          <SkeletonItem
            className={styles.root_dropdown_skeleton}
            isLoading={isLoading}
            value={
              <AppMultiSelectDropDown
                field={field}
                options={options}
                hasGroupedOptions={hasGroupedOptions}
                disabled={disabled}
                filter={filter}
                editable={editable}
                className={styles.root_dropdown}
                invalid={fieldState.error != null}
                defaultValue={defaultValue}
                onFocus={onFocus}
                onBlur={onBlur}
                filterBy={filterBy}
                includeId={includeId}
                selectAllOption={selectAllOption}
                noneOption={noneOption}
                emptyMessage={emptyMessage}
                display={display}
                showPanelHeader={showPanelHeader}
                virtualScrollerOptions={virtualScrollerOptions}
                optionDisabled={optionDisabled}
                itemTemplate={itemTemplate}
                panelClassName={panelClassName}
                hiddenValues={hiddenValues}
                maxSelectedLabels={maxSelectedLabels}
                selectedItemsLabel={selectedItemsLabel}
              />
            }
          />
        </div>
      )}
    />
  );
};
